/**
 * Les espaces de l'application.
 *
 * <p>Un compte peut être professeur, inspecteur et administrateur à la fois.
 * Chaque rôle de base ouvre un espace — Enseignant, Administration, Élève — et
 * un seul espace s'ouvre par une permission : Inspection, par `sessions.inspect`.
 * Un espace a son menu, sa route d'accueil, son icône ; le sélecteur « Continuer
 * en tant que » passe de l'un à l'autre sans se déconnecter.</p>
 */
export type Space = 'admin' | 'teacher' | 'inspection' | 'student';

export interface SpaceDefinition {
  id: Space;
  /** Préfixe de route : c'est lui qui dit dans quel espace on se trouve. */
  prefix: string;
  /** Où l'on arrive en entrant dans l'espace. */
  home: string;
  /** Classe Font Awesome, comme les entrées de menu. */
  icon: string;
  labelKey: string;
  hintKey: string;
}

/** Dans l'ordre d'affichage du sélecteur. */
export const SPACES: readonly SpaceDefinition[] = [
  { id: 'admin', prefix: '/admin', home: '/admin/dashboard', icon: 'fa-solid fa-user-shield', labelKey: 'SPACES.ADMIN', hintKey: 'SPACES.ADMIN_HINT' },
  { id: 'teacher', prefix: '/teacher', home: '/teacher/dashboard', icon: 'fa-solid fa-chalkboard-user', labelKey: 'SPACES.TEACHER', hintKey: 'SPACES.TEACHER_HINT' },
  { id: 'inspection', prefix: '/inspector', home: '/inspector/sessions', icon: 'fa-solid fa-clipboard-check', labelKey: 'SPACES.INSPECTION', hintKey: 'SPACES.INSPECTION_HINT' },
  { id: 'student', prefix: '/student', home: '/student/dashboard', icon: 'fa-solid fa-graduation-cap', labelKey: 'SPACES.STUDENT', hintKey: 'SPACES.STUDENT_HINT' },
];

export function spaceOf(id: Space): SpaceDefinition {
  return SPACES.find((s) => s.id === id)!;
}

/** L'espace dans lequel une URL se trouve, s'il y en a un. */
export function spaceFromUrl(url: string): Space | null {
  const path = url.split('?')[0].split('#')[0];
  return SPACES.find((s) => path === s.prefix || path.startsWith(s.prefix + '/'))?.id ?? null;
}
